import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { StorageKey } from '@/types/storage.types';

export const useSessionState = <T,>(key: string, defaultValue: T) => {
  const subscribe = useCallback(
    (callback: () => void) => {
      const handleStorageChange = (e: Event) => {
        if (e instanceof CustomEvent && e.detail?.key === key) {
          callback();
        }
      };

      window.addEventListener('session-change', handleStorageChange);
      return () => window.removeEventListener('session-change', handleStorageChange);
    },
    [key],
  );

  const getSnapshot = useCallback(() => {
    return sessionStorage.getItem(key);
  }, [key]);

  const setSessionState = useCallback(
    (payload: T) => {
      if (typeof window === 'undefined') return;

      if (payload === null || payload === '') {
        sessionStorage.removeItem(key);
      } else {
        sessionStorage.setItem(key, JSON.stringify(payload));
      }

      // Dispatch a type-safe custom event
      const event = new CustomEvent('session-change', {
        detail: { key },
      });
      window.dispatchEvent(event);
    },
    [key],
  );

  const stateRaw = useSyncExternalStore(subscribe, getSnapshot, () => null);
  const state = useMemo(() => (stateRaw ? (JSON.parse(stateRaw) as T) : defaultValue), [stateRaw, defaultValue]);
  return [state, setSessionState] as const;
};

export const useLocalStorageState = <T,>(key: StorageKey, defaultValue: T) => {
  const subscribe = useCallback(
    (callback: () => void) => {
      const handleStorageChange = (e: Event) => {
        if (e instanceof CustomEvent && e.detail?.key === key) {
          callback();
        }
      };

      window.addEventListener(`ls-update-${key}`, handleStorageChange);
      return () => window.removeEventListener(`ls-update-${key}`, handleStorageChange);
    },
    [key],
  );

  const getSnapshot = useCallback(() => {
    return localStorage.getItem(key);
  }, [key]);

  const setLocalStorageState = useCallback(
    (payload: T | ((prev: T) => T)) => {
      if (typeof window === 'undefined') return;

      let valueToStore: T;

      if (typeof payload === 'function') {
        const currentRaw = localStorage.getItem(key);
        let currentValue: T;
        try {
          currentValue = currentRaw ? (JSON.parse(currentRaw) as T) : defaultValue;
        } catch {
          return;
        }
        valueToStore = (payload as (prev: T) => T)(currentValue);
      } else {
        valueToStore = payload;
      }

      if (valueToStore === null || valueToStore === '' || valueToStore === undefined) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(valueToStore));
      }

      const event = new CustomEvent(`ls-update-${key}`, {
        detail: { key },
      });
      window.dispatchEvent(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, JSON.stringify(defaultValue)],
  );

  const stateRaw = useSyncExternalStore(subscribe, getSnapshot, () => null);
  const state = useMemo(
    () => (stateRaw ? (JSON.parse(stateRaw) as T) : defaultValue),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stateRaw, JSON.stringify(defaultValue)],
  );
  return [state, setLocalStorageState] as const;
};
